import { CSSProperties, useEffect, useState } from 'react'
import { ConfigProvider, InputNumber, Slider, theme } from 'antd'
import { BUILDER_ACCENT_COLOR } from '../constants'
import { FieldWarning } from '../inputSanityModel'
import { SliderTrack, clampToTrack, fitsTrack, opensInTypedBox } from '../bandSliderModel'
import InputWarning from './InputWarning'
import './BandSliderInput.css'

type BandUnit = 'dollar' | 'percent' | 'number'

type Props = {
  value: number | null
  onChange: (value: number | null) => void
  track: SliderTrack
  unit?: BandUnit
  formatValue?: (value: number) => string
  valueSuffix?: string
  placeholder?: string
  helpText?: string
  allowCustom?: boolean
  compact?: boolean
  warning?: FieldWarning | null
  warnOnTrack?: boolean
}

const withCommas = (value: number | string): string =>
  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

const defaultFormat = (unit: BandUnit) => (value: number): string => {
  if (unit === 'percent') {
    return `${value}%`
  }
  if (unit === 'number') {
    return withCommas(value)
  }
  return `$${withCommas(value)}`
}

const BandSliderInput = ({
  value,
  onChange,
  track,
  unit = 'dollar',
  formatValue,
  valueSuffix,
  placeholder,
  helpText,
  allowCustom = true,
  compact = false,
  warning = null,
  warnOnTrack = false
}: Props) => {
  const { token } = theme.useToken()
  const [requestedCustom, setRequestedCustom] = useState(false)
  const custom = requestedCustom || opensInTypedBox(value, track, allowCustom)

  useEffect(() => {
    if (allowCustom || fitsTrack(value, track)) {
      return
    }
    onChange(clampToTrack(value, track))
  }, [allowCustom, value, track, onChange])

  const format = formatValue ?? defaultFormat(unit)
  const thumbValue = clampToTrack(value, track)

  const palette = {
    '--band-slider-value-color': BUILDER_ACCENT_COLOR,
    '--band-slider-muted': token.colorTextTertiary,
    '--band-slider-font-size': `${token.fontSizeSM}px`,
    '--band-slider-value-size': `${compact ? token.fontSize : token.fontSizeHeading3}px`,
    '--band-slider-gap': `${token.marginXXS}px`
  } as CSSProperties

  const help = helpText === undefined
    ? null
    : <p className="band-slider-help">{helpText}</p>

  const escapeHatch = (label: string, onClick: () => void) => (
    <button type="button" className="band-slider-hatch" onClick={onClick}>
      {label}
    </button>
  )

  if (custom) {
    return (
      <div className="band-slider" style={palette}>
        <InputNumber
          value={value}
          onChange={onChange}
          prefix={unit === 'dollar' ? '$' : undefined}
          suffix={unit === 'percent' ? '%' : undefined}
          formatter={unit === 'dollar' ? (v) => withCommas(`${v}`) : undefined}
          parser={unit === 'dollar' ? (v) => Number(`${v}`.replace(/\$\s?|(,*)/g, '')) : undefined}
          placeholder={placeholder}
          style={{ width: '100%' }}
          size={compact ? 'middle' : 'large'}
        />
        {help}
        <InputWarning warning={warning} />
        {escapeHatch(compact ? 'Use the slider' : 'Use the slider instead', () => {
          onChange(clampToTrack(value, track))
          setRequestedCustom(false)
        })}
      </div>
    )
  }

  return (
    <div className="band-slider" style={palette}>
      <div className="band-slider-track">
        {!compact && <span className="band-slider-bound">{format(track.min)}</span>}
        <ConfigProvider theme={{ token: { colorPrimary: BUILDER_ACCENT_COLOR } }}>
          <Slider
            min={track.min}
            max={track.max}
            step={track.step}
            value={thumbValue}
            onChange={onChange}
            tooltip={{ open: false }}
            style={{ flex: 1 }}
          />
        </ConfigProvider>
        {!compact && <span className="band-slider-bound">{format(track.max)}</span>}
      </div>

      <div className={compact ? 'band-slider-value band-slider-value-compact' : 'band-slider-value'}>
        <span className="band-slider-amount">{format(thumbValue)}</span>
        {valueSuffix !== undefined && <span className="band-slider-suffix">{valueSuffix}</span>}
      </div>

      {help}

      {warnOnTrack && <InputWarning warning={warning} />}

      {allowCustom && escapeHatch(
        compact ? 'Not on this slider' : "My number isn't on this slider",
        () => setRequestedCustom(true)
      )}
    </div>
  )
}

export default BandSliderInput
